import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * EditModeContext
 * --------------------------------------------------------------------
 * Centralized state for the inline CMS editor overlay.
 *
 * Shape:
 *   {
 *     isAdmin:      boolean   - is the current viewer an admin / editor
 *     editMode:     boolean   - is edit overlay currently visible
 *     toggleEditMode(): void
 *     setDirty(uid):  void    - mark a block as having unsaved changes
 *     savePending:    Set     - set of uids currently saving / dirty
 *     save(uid, data): Promise
 *     upload(file):    Promise<{ url }>
 *   }
 *
 * All handlers default to safe no-ops so the components can be rendered
 * outside the provider during local development.
 *
 * The provider accepts overrides via props so the admin shell (added by
 * a later agent) can wire real implementations:
 *
 *   <EditModeProvider
 *      isAdmin={user.isAdmin}
 *      save={async (uid, data) => api.saveBlock(uid, data)}
 *      upload={async (file) => api.uploadAsset(file)}
 *   >
 *     <App />
 *   </EditModeProvider>
 */

const noopAsync = async () => ({});
const noopUpload = async () => ({ url: '' });

const defaultValue = {
  isAdmin: false,
  editMode: false,
  toggleEditMode: () => {},
  setDirty: () => {},
  savePending: new Set(),
  save: noopAsync,
  upload: noopUpload,
};

const EditModeContext = createContext(defaultValue);

export function EditModeProvider({
  children,
  isAdmin = false,
  initialEditMode = false,
  save: saveProp,
  upload: uploadProp,
}) {
  const [editMode, setEditMode] = useState(Boolean(initialEditMode));
  const [savePending, setSavePending] = useState(() => new Set());

  const toggleEditMode = useCallback(() => {
    setEditMode((v) => !v);
  }, []);

  const setDirty = useCallback((uid) => {
    if (!uid) return;
    setSavePending((prev) => {
      if (prev.has(uid)) return prev;
      const next = new Set(prev);
      next.add(uid);
      return next;
    });
  }, []);

  const clearDirty = useCallback((uid) => {
    setSavePending((prev) => {
      if (!prev.has(uid)) return prev;
      const next = new Set(prev);
      next.delete(uid);
      return next;
    });
  }, []);

  const save = useCallback(
    async (uid, data) => {
      try {
        const result = saveProp ? await saveProp(uid, data) : await noopAsync();
        clearDirty(uid);
        return result;
      } catch (err) {
        // Keep dirty on failure so the user can retry.
        throw err;
      }
    },
    [saveProp, clearDirty]
  );

  const upload = useCallback(
    async (file) => {
      if (uploadProp) return uploadProp(file);
      // Fallback: produce a local object URL so previews work without backend.
      if (file instanceof File || file instanceof Blob) {
        return { url: URL.createObjectURL(file) };
      }
      return { url: '' };
    },
    [uploadProp]
  );

  const value = useMemo(
    () => ({
      isAdmin,
      editMode: isAdmin && editMode,
      toggleEditMode,
      setDirty,
      savePending,
      save,
      upload,
    }),
    [isAdmin, editMode, toggleEditMode, setDirty, savePending, save, upload]
  );

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}

export function useEditMode() {
  return useContext(EditModeContext);
}

export default EditModeContext;
